import type React from 'react';
import { useState } from 'react';

import { useGetEvent, useGetFace, useGetFaceAttributeSamples } from '@vlabs/api/artemy/client';
import type { EventId } from '@vlabs/api/artemy/model';
import { useHasPermission, usePageAccess } from '@vlabs/permissions';
import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import {
  AdditionalPropertiesBySampleId,
  TopMatchCardInfoPoint3,
  TopMatchFaceAttributes,
} from '@vlabs/monokit/entities/event-details';
import { GoToCardButton } from '@vlabs/monokit/features/go-to-card';
import { FindSimilarDropdownPoint3 } from '@vlabs/monokit/features/point/find-similar';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useTopMatchCardPointTranslation } from './i18n';
import st from './TopMatchCardPoint.module.sass';

interface TopMatchCardPoint3Props extends WithDataTestid, WithClassNameProp {
  eventId: EventId;
}

type Tab = 'attributes' | 'additional_properties';

const displayName = 'TopMatchCardPoint3';
const testid = buildTestid(displayName);

function TopMatchCardPoint3({
  eventId,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: TopMatchCardPoint3Props): React.JSX.Element | null {
  const { t } = useTopMatchCardPointTranslation();
  const [tab, setTab] = useState<Tab>('attributes');
  const canReadCard = useHasPermission('card', 'read');
  const canViewCardDetails = usePageAccess('cards_details');
  const { data: event } = useGetEvent(eventId, { target: ['top_match'] });

  // для Point топ-матч показываем только как карточку, нужен face top_match
  const topMatch = event?.top_match;
  const faceId = topMatch && 'face_id' in topMatch ? topMatch.face_id : undefined;
  // external_id проверяем по актуальному лицу
  const { data: face } = useGetFace(
    faceId ?? '',
    { targets: ['card_id'] },
    { query: { enabled: !!faceId } },
  );
  const { data: faceAttributeSamples } = useGetFaceAttributeSamples(faceId ?? '', {
    query: { enabled: !!faceId && tab === 'additional_properties' },
  });
  const externalId = face?.card_id;
  const sampleId = faceAttributeSamples?.samples?.[0];

  if (!canReadCard) return null;
  if (!faceId || !externalId) return null;

  let additionalContent = null;
  if (tab === 'additional_properties') {
    if (sampleId) {
      additionalContent = (
        <AdditionalPropertiesBySampleId
          className={st.AdditionalProperties}
          sampleId={sampleId}
          {...testid(dataTestId)}
        />
      );
    } else {
      additionalContent = <span className={st.Empty}>{t('no_data')}</span>;
    }
  }

  return (
    <div
      className={cn(st.Root, className)}
      {...testid(dataTestId)}
    >
      <h5>{t('title')}</h5>
      <div className={st.Panel}>
        <div className={st.Header}>
          <ButtonSwitchGroup
            onChange={setTab}
            size="small"
            value={tab}
            variant="square"
            {...testid('Tabs', dataTestId)}
          >
            <ButtonSwitch value="attributes">
              {t('attributes')}
            </ButtonSwitch>
            <ButtonSwitch value="additional_properties">
              {t('additional_properties')}
            </ButtonSwitch>
          </ButtonSwitchGroup>

          <div className={st.Actions}>
            {!!canViewCardDetails && (
              <GoToCardButton
                cardId={externalId}
                variant="filled"
                {...testid(dataTestId)}
              />
            )}
            <FindSimilarDropdownPoint3
              eventId={eventId}
              type="top match"
              {...testid(dataTestId)}
            />
          </div>
        </div>

        <div className={st.Content}>
          <TopMatchCardInfoPoint3
            faceId={faceId}
            similarity={topMatch.similarity}
            {...testid(dataTestId)}
          />
          {tab === 'attributes' && (
            <div className={st.AttributesContent}>
              <TopMatchFaceAttributes
                className={st.Attributes}
                faceId={faceId}
                {...testid(dataTestId)}
              />
            </div>
          )}
          {additionalContent}
        </div>
      </div>
    </div>
  );
}

TopMatchCardPoint3.displayName = displayName;

export { TopMatchCardPoint3 };
